import { ViewController } from 'elc-service';
import { Shade } from '../domain/entities/Shade';
import { SHADE_PICKER_TYPE } from '../constants/ShadePickerType';
import { SHADE_INFO_POSITION_TYPE } from '../constants/ShadeInfoPositionType';
import { PERFECT_SHADE_CIRCLE_COUNT, SHADE_CIRCLE_COUNT } from '../constants/ProductShadePicker';
import {
    ICarouselSettings,
    IShadeSwatches,
    IresponsiveConfig,
    IShadeSwatchesBreakpointsType,
    IShadePickerProps
} from '../interfaces/IShadePicker';
import { Breakpoints } from 'elc-base-theme';
import { action, computed, observable } from 'mobx';
import { SHADE_GROUP } from '../constants/ShadeGroupOptions';
import { IShadesByShadeGroup } from '../domain/entities/Product';
import { emit, events } from 'elc-service-bus';
import {
    trackingShadeGroupEventAction,
    trackingShadeGroupEventName
} from '../constants/TrackingEvents';

export class ProductShadePickerViewController extends ViewController {
    @observable private shades: Shade[] = [];
    @observable public selectedShadeGroupKey: string = SHADE_GROUP.ALL;
    private originalShades: Shade[] = [];
    private shadesByShadeGroup: IShadesByShadeGroup[] = [];
    private perfectShades: Shade[] = [];
    private shadeSwatchesConfig: IShadeSwatches;
    private responsiveShadeSwatchesConfig: IresponsiveConfig;
    private responsiveShadeSwatchesCategoriesConfig: IresponsiveConfig;
    private isPerfectShadeEnabled: boolean = false;
    private componentType: string = '';

    public initialize = (shadePickerProps: IShadePickerProps) => {
        this.shades = shadePickerProps.shades;
        this.originalShades = shadePickerProps.shades;
        this.shadesByShadeGroup = shadePickerProps.shadesByShadeGroup || [];
        this.perfectShades = shadePickerProps.perfectShades || [];
        this.isPerfectShadeEnabled = Boolean(shadePickerProps.isPerfectShadeEnabled);
        this.setShadeSwatchConfig(shadePickerProps);
        this.setResponsiveShadeSwatchConfig();
        this.setShadeSwatchCategoriesConfig();
        this.setComponentType(shadePickerProps);
    };

    @action
    private setShadeSwatchConfig(shadePickerProps: IShadePickerProps) {
        const { componentType, config: { shadeSwatches } = {} } = shadePickerProps;
        const { overrides, ...shadeSwatchesConfig } = shadeSwatches || {};
        const shadeSwatchesOverrides =
            (shadePickerProps && shadeSwatchesConfig && overrides && overrides[componentType]) ||
            {};

        this.shadeSwatchesConfig = { ...shadeSwatchesConfig, ...shadeSwatchesOverrides };
    }

    @action
    private setResponsiveShadeSwatchConfig() {
        const { breakpoints, categories, ...config } = this.shadeSwatchesSettings || {};
        const desktop = breakpoints && breakpoints.desktop;
        const largeScreen = breakpoints && breakpoints.largeScreen;
        const extraLargeScreen = breakpoints && breakpoints.extraLargeScreen;

        const desktopConfig = { ...config, ...desktop };
        const largeScreenConfig = { ...desktopConfig, ...largeScreen };
        const extraLargeScreenConfig = { ...largeScreenConfig, ...extraLargeScreen };

        this.responsiveShadeSwatchesConfig = {
            mobile: config as IShadeSwatchesBreakpointsType,
            desktop: desktopConfig as IShadeSwatchesBreakpointsType,
            largeScreen: largeScreenConfig as IShadeSwatchesBreakpointsType,
            extraLargeScreen: extraLargeScreenConfig as IShadeSwatchesBreakpointsType
        };
    }

    @action
    private setShadeSwatchCategoriesConfig() {
        const { categories } = this.shadeSwatchesSettings || {};
        const mobileConfig = categories as IShadeSwatchesBreakpointsType;
        const desktopConfig = this.responsiveShadeSwatchesConfig
            .desktop as IShadeSwatchesBreakpointsType;
        const largeScreenConfig = this.responsiveShadeSwatchesConfig
            .largeScreen as IShadeSwatchesBreakpointsType;
        const extraLargeScreenConfig = this.responsiveShadeSwatchesConfig
            .desktop as IShadeSwatchesBreakpointsType;

        const desktopCategoriesConfig = { ...categories, ...desktopConfig.categories };
        const largeScreenCategoriesConfig = {
            ...desktopCategoriesConfig,
            ...largeScreenConfig.categories
        };
        const extraLargeScreenCategoriesConfig = {
            ...largeScreenCategoriesConfig,
            ...extraLargeScreenConfig.categories
        };
        this.responsiveShadeSwatchesCategoriesConfig = {
            mobile: mobileConfig,
            desktop: desktopCategoriesConfig,
            largeScreen: largeScreenCategoriesConfig,
            extraLargeScreen: extraLargeScreenCategoriesConfig
        };
    }

    @computed
    private get activeShades(): Shade[] {
        const showGroupShades =
            this.selectedShadeGroupKey &&
            this.selectedShadeGroupKey.length > 0 &&
            this.selectedShadeGroupKey !== SHADE_GROUP.ALL;

        if (showGroupShades) {
            const selectedGroup = this.shadesByShadeGroup.find(
                (shadesByGroup) =>
                    shadesByGroup.productShadeGroup.key === this.selectedShadeGroupKey
            );

            return selectedGroup ? selectedGroup.shades : this.originalShades;
        }

        return this.originalShades;
    }

    private setComponentType(config: IShadePickerProps) {
        this.componentType = config && config.componentType;
    }

    @action
    public setShadeGroup = (shadeGroupKey: string) => {
        this.selectedShadeGroupKey = shadeGroupKey;
        this.updateShades(this.filteredShades);

        emit(events.PRODUCT_SHADE_GROUP_SELECTED, {
            shadeGroupKey: shadeGroupKey,
            eventName: trackingShadeGroupEventName[this.componentType],
            eventAction: trackingShadeGroupEventAction[this.componentType]
        });
    };

    @computed
    public get filteredShades(): Shade[] {
        return this.isPerfectShadeEnabled && this.perfectShades.length
            ? this.perfectShades
            : this.activeShades || this.shades;
    }

    public get hasMultipleShades(): boolean {
        return this.originalShades.length > 1;
    }

    public get hasSomeInsideShadeCircle(): boolean {
        return this.shades.some((shade) => shade.showNameInsideShadeCircle);
    }

    public get slideCount(): number {
        return this.hasSomeInsideShadeCircle ? PERFECT_SHADE_CIRCLE_COUNT : SHADE_CIRCLE_COUNT;
    }

    public get responsiveCarouselSettings(): ICarouselSettings {
        return {
            dots: false,
            arrows: true,
            infinite: false,
            speed: 500,
            responsive: [
                {
                    breakpoint: Breakpoints.desktop,
                    settings: {
                        slidesToShow: this.slideCount,
                        slidesToScroll: this.slideCount
                    }
                }
            ],
            lazyLoad: true,
            ...this.shadeSwatchesConfig.carousel
        };
    }

    public get isShadeSwatchesTypeCarousel(): boolean {
        return this.shadeSwatchesConfig.type === SHADE_PICKER_TYPE.CAROUSEL;
    }

    public get isShadeSwatchesTypeGrid(): boolean {
        return this.shadeSwatchesConfig.type === SHADE_PICKER_TYPE.GRID;
    }

    public get responsiveShadeSwatchesCategoriesSettings(): IresponsiveConfig {
        return this.responsiveShadeSwatchesCategoriesConfig;
    }

    public get responsiveShadeSwatchesConfigSettings(): IresponsiveConfig {
        return this.responsiveShadeSwatchesConfig;
    }

    public get shadeSwatchesSettings(): IShadeSwatches {
        return this.shadeSwatchesConfig;
    }

    public get shadeNamePosition(): SHADE_INFO_POSITION_TYPE | undefined {
        return this.shadeSwatchesSettings.shadeName;
    }

    public get isShadeInfoAbovePicker(): boolean {
        return this.shadeNamePosition === SHADE_INFO_POSITION_TYPE.ABOVE;
    }

    public get isShadeInfoBelowPicker(): boolean {
        return this.shadeNamePosition === SHADE_INFO_POSITION_TYPE.BELOW;
    }

    public get isShadeInfoInline(): boolean {
        return this.shadeNamePosition === SHADE_INFO_POSITION_TYPE.INLINE;
    }

    public get shadeDescriptionPosition() {
        return this.shadeSwatchesSettings.shadeDescription;
    }

    @action
    public updateShades(shades: Shade[]) {
        this.shades = shades;
    }
}
